/**
 * HTTP client.
 *
 * Two rules this file exists to keep:
 *
 *   1. Responses are NOT parsed with anything that touches numbers. `JSON.parse`
 *      would turn a money field into a float if the server ever sent one
 *      unquoted, so amounts are typed as `string` throughout and this client
 *      never coerces. The contract guarantees decimal strings; if one arrives as
 *      a number that is a server bug and should be visible, not smoothed over.
 *   2. Every error becomes an `ApiError` carrying the contract's error code, so
 *      screens can react to `insufficient_funds` or `invalid_state` by name
 *      rather than by matching on a message.
 */
import { authHeaders, hasCredential, tryRefresh } from './auth.ts';

const BASE = ((import.meta.env['VITE_API_BASE'] as string | undefined) ?? '').replace(/\/$/, '');

/** Error codes from docs/02-API-CONTRACT.md. */
export type ErrorCode =
  | 'validation_error'
  | 'unauthorized'
  | 'account_inactive'
  | 'insufficient_funds'
  | 'duplicate_settlement'
  | 'no_method'
  | 'not_found'
  | 'invalid_state'
  | 'payments_unavailable'
  /** T-143. A candidate key was refused by the pre-activation check; nothing was written. */
  | 'secret_rejected'
  /** T-143. `SECRETS_MASTER_KEY` is unset or the wrong length, so the key manager is off. */
  | 'secrets_unavailable'
  | 'forbidden'
  | 'network_error'
  | 'unknown';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;

  constructor(code: ErrorCode, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const send = async (): Promise<Response> => {
    try {
      return await fetch(url.toString(), {
        method: options.method ?? 'GET',
        headers: {
          Accept: 'application/json',
          ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...authHeaders(),
        },
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (cause) {
      throw new ApiError('network_error', 'Could not reach the API.', 0);
    }
  };

  let response = await send();

  // The access token is short-lived by design. One rotation, one retry; a
  // second 401 means the session is over and `tryRefresh` has already ended it.
  if (response.status === 401 && hasCredential() && (await tryRefresh())) {
    response = await send();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const shape = payload as { error?: string; message?: string } | null;
    throw new ApiError(
      (shape?.error as ErrorCode) ?? 'unknown',
      shape?.message ?? `Request failed with ${response.status}.`,
      response.status,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, query === undefined ? {} : { query }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body }) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', ...(body === undefined ? {} : { body }) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', ...(body === undefined ? {} : { body }) }),
  /** Used by the edge editor to drop a staged change or release an override. */
  del: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', ...(query === undefined ? {} : { query }) }),
};
